import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, api } from '../context/AuthContext';
import { Work, LocationOn, AttachMoney } from '@mui/icons-material';

function JobDetails() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [resume, setResume] = useState(null);

  const [resumeName, setResumeName] = useState('');

  const [education, setEducation] = useState('');
  const [experience, setExperience] = useState('');
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showFinalForm, setShowFinalForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
      async function fetchJob() {
        try {
          const response = await api.get(`/jobs/${id}`);
          setJob(response.data);

          const qs = response.data?.screening_questions;
          if (Array.isArray(qs)) {
            setScreeningQuestions(qs);
          } else if (typeof qs === 'string') {
            try {
              setScreeningQuestions(JSON.parse(qs));
            } catch (e) {
              setScreeningQuestions([]);
            }
          } else {
            setScreeningQuestions([]);
          }

          // ADDED: Check application status if user is logged in
          if (isAuthenticated) {
            const statusCheck = await api.get(`/applications/check/${id}`);
            if (statusCheck.data.hasApplied) {
              setHasApplied(true);
            }
          }
        } catch (error) {
          console.error('Failed to fetch job:', error);
        } finally {
          setLoading(false);
        }
      }
      fetchJob();
    }, [id, isAuthenticated]); // Added isAuthenticated to dependency array

    const resetApplyFlow = () => {
      setError('');
      setSuccess('');
      setCurrentQuestionIndex(0);
      setShowFinalForm(false);
      setScreeningAnswers({});
      setEducation('');
      setExperience('');
      setResume(null);

      setResumeName('');
    };

    const handleScreeningAnswerChange = (key, value) => {
      setScreeningAnswers((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const getExperienceYears = () => {
      const rawValue =
        screeningAnswers.totalExperience ||
        screeningAnswers.experience ||
        experience ||
        '';

      const text = String(rawValue).trim().toLowerCase();
      if (!text) return 0;
      if (text.includes('fresher')) return 0;

      const match = text.match(/(\d+(\.\d+)?)/);
      if (match) return Number(match[1]);

      return text ? 1 : 0;
    };

    const currentQuestion = screeningQuestions[currentQuestionIndex];

    const hasScreeningQuestions =
      Array.isArray(job?.screening_questions) &&
      job.screening_questions
        .map((q) => String(q).trim())
        .filter(Boolean).length > 0;

    const handleNextScreening = () => {
      setError('');
      setSuccess('');

      if (!screeningQuestions.length) {
        setShowFinalForm(true);
        return;
      }

      if (!currentQuestion) {
        setShowFinalForm(true);
        return;
      }

      if (currentQuestion.key === 'noticePeriod') {
        const years = getExperienceYears();
        if (years <= 0) {
          if (currentQuestionIndex < screeningQuestions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
          } else {
            setShowFinalForm(true);
          }
          return;
        }
      }

      const answer = screeningAnswers[currentQuestion.key];
      if (!answer || !String(answer).trim()) {
        setError('Please answer the current question');
        return;
      }

      if (currentQuestionIndex < screeningQuestions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setShowFinalForm(true);
      }
    };

  async function handleApply() {
    setError('');
    setSuccess('');

    if (!isAuthenticated) {
      alert('Please login first to apply.');
      navigate('/login');
      return;
    }

    if (!education.trim()) {
      setError("Education is required");
      return;
    }

    if (!experience.trim()) {
      setError("Previous experience is required");
      return;
    }

    if (!resume) {
      setError("Resume is required");
      return;
    }
try {


  const formData = new FormData();

  formData.append("jobId", id);
  formData.append("education", education);
  formData.append("experience", experience);
  formData.append("screening_answers", JSON.stringify(screeningAnswers));
  formData.append("resume", resume);

  await api.post("/applications", formData);

  setSuccess("Application submitted successfully!");

  setTimeout(() => {
      setApplyDialogOpen(false);
      setHasApplied(true);
      resetApplyFlow();
  }, 1500);

} catch (err) {
  setError(err.response?.data?.message || 'Failed to submit application');
}
}


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Job not found</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>



      <Paper sx={{ p: 4 }}>
        <Typography variant="h3" gutterBottom>
          {job.title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip icon={<Work />} label={job.company_name} color="primary" />
          {job.location && (
            <Chip icon={<LocationOn />} label={job.location} variant="outlined" />
          )}
          {job.salary && (
            <Chip icon={<AttachMoney />} label={job.salary} variant="outlined" />
          )}
          {job.stipend && (
            <Chip label={`Stipend: ₹${job.stipend}`} variant="outlined" color="secondary" />
          )}
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Description
        </Typography>
        <Box 
          sx={{ 
            '& h1': { fontSize: '2rem', mt: 2, mb: 1 },
            '& h2': { fontSize: '1.5rem', mt: 2, mb: 1 },
            '& h3': { fontSize: '1.25rem', mt: 2, mb: 1 },
            '& ul, & ol': { pl: 4, mb: 2 },
            '& li': { mb: 0.5 },
            '& p': { mb: 1 },
            wordBreak: 'break-word'
          }}
          dangerouslySetInnerHTML={{ __html: job.description }}
        />

        {job.requirements && job.requirements.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Requirements
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              {job.requirements.map((req, index) => (
                <Typography component="li" key={index} sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                  {req}
                </Typography>
              ))}
            </Box>
          </>
        )}

        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            // UPDATED: Disable if hasApplied is true
            disabled={hasApplied}
            // UPDATED: Change color if applied
            color={hasApplied ? "success" : "primary"}
            onClick={() => {
              if (hasApplied) return;
              if (isAuthenticated) {
                resetApplyFlow();
                setShowFinalForm(!hasScreeningQuestions);
                setApplyDialogOpen(true);
              } else {
                alert('Please login first to apply.');
                navigate('/login');
              }
            }}
            sx={{
              textTransform: 'uppercase', // Matches your screenshot "APPLIED"
              fontWeight: 'bold',
              borderRadius: '8px', // Slightly rounded edges like the chips
              px: 4,
              // The teal color from your Amazon chip
              backgroundColor: '#4F46E5 !important',
              color: '#ffffff !important',
              boxShadow: '0px 4px 10px rgba(0, 139, 139, 0.3)', // Flat look like the chip
              '&:hover': {
                backgroundColor: '#007070 !important',

              },
              '&.Mui-disabled': {
                // Keeps the teal color when disabled but slightly transparent
                backgroundColor: '#4F46E5 !important',
                color: '#ffffff !important',
                opacity: 0.9,
                cursor: 'not-allowed',
              },
            }}
          >
            {/* UPDATED: Toggle button text */}
            {hasApplied ? 'Applied' : 'Apply Now'}
          </Button>
        </Box>
      </Paper>

      {/* Apply Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={() => {
          setApplyDialogOpen(false);
          resetApplyFlow();
        }}
        maxWidth="md"
        fullWidth
      >
 <DialogTitle
  sx={{
    fontWeight: 800,
    fontSize: '1.35rem',
    color: '#111827',
    borderBottom: '1px solid #f1f5f9',
    pb: 2
  }}
>
  Complete Your Application
</DialogTitle>
        <DialogContent>
         {error && (
           <Alert
             severity="error"
             sx={{ mb: 2 }}
           >
             {error}
           </Alert>
         )}

         {success && (
           <Alert
             severity="success"
             sx={{ mb: 2 }}
           >
             {success}
           </Alert>
         )}



         {hasScreeningQuestions ? (
           !showFinalForm ? (
             <>
               <Box
                 sx={{
                   mb: 3,
                   p: 3,
                   borderRadius: '20px',
                   background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                   border: '1px solid #e2e8f0',
                   boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)'
                 }}
               >
                 <Typography
                   sx={{
                     fontSize: '0.95rem',
                     fontWeight: 800,
                     color: '#1e293b',
                     mb: 0.75
                   }}
                 >
                   Experience & Background
                 </Typography>

                 <Typography
                   sx={{
                     fontSize: '0.92rem',
                     color: '#475569',
                     lineHeight: 1.7,
                     mb: 0.75
                   }}
                 >
                   Please answer a few short questions to complete your application.
                 </Typography>

                 <Typography
                   sx={{
                     fontSize: '0.8rem',
                     color: '#64748b'
                   }}
                 >
                   Your responses will be shared with the recruiter along with your application.
                 </Typography>
               </Box>

               <LinearProgress
                 variant="determinate"
                 value={
                   screeningQuestions.length
                     ? ((currentQuestionIndex + 1) / screeningQuestions.length) * 100
                     : 0
                 }
                 sx={{
                   height: 8,
                   borderRadius: 999,
                   mb: 3,
                   backgroundColor: '#e2e8f0',
                   '& .MuiLinearProgress-bar': {
                     borderRadius: 999,
                     background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)'
                   }
                 }}
               />

               {currentQuestion ? (
                 <Box
                   sx={{
                     p: 3,
                     borderRadius: '20px',
                     backgroundColor: '#ffffff',
                     border: '1px solid #e2e8f0',
                     boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                     mb: 3
                   }}
                 >
                   <Typography
                     sx={{
                       fontSize: '0.8rem',
                       fontWeight: 800,
                       color: '#2563eb',
                       textTransform: 'uppercase',
                       letterSpacing: '0.06em',
                       mb: 1
                     }}
                   >
                     Question {currentQuestionIndex + 1} of {screeningQuestions.length}
                   </Typography>

                   <Typography
                     sx={{
                       fontSize: '1rem',
                       fontWeight: 700,
                       color: '#0f172a',
                       lineHeight: 1.7,
                       mb: 2
                     }}
                   >
                     {currentQuestion.question}
                   </Typography>

                   <TextField
                     fullWidth
                     value={screeningAnswers[currentQuestion.key] || ''}
                     onChange={(e) =>
                       handleScreeningAnswerChange(currentQuestion.key, e.target.value)
                     }
                     multiline
                     rows={4}
                     placeholder="Type your answer here..."
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: '14px',
                         backgroundColor: '#f8fafc',
                         '& fieldset': {
                           borderColor: '#cbd5e1'
                         },
                         '&:hover fieldset': {
                           borderColor: '#94a3b8'
                         },
                         '&.Mui-focused fieldset': {
                           borderColor: '#2563eb'
                         }
                       },
                       '& .MuiInputBase-input': {
                         fontSize: '0.96rem',
                         lineHeight: 1.8,
                         color: '#0f172a'
                       }
                     }}
                   />
                 </Box>
               ) : null}
             </>
           ) : (
             <>
               <TextField
                 fullWidth
                 label="Education"
                 multiline
                 rows={2}
                 value={education}
                 onChange={(e) => setEducation(e.target.value)}
                 margin="normal"
                 required
                 placeholder="E.g. B.Tech in Computer Science from XYZ University"
               />

               <TextField
                 fullWidth
                 label="Previous Experience"
                 multiline
                 rows={2}
                 value={experience}
                 onChange={(e) => setExperience(e.target.value)}
                 margin="normal"
                 required
                 placeholder="E.g. 2 years as Software Developer at ABC Corp"
               />

               <Box sx={{ mt: 2, mb: 1 }}>
                 <Button variant="outlined" component="label" fullWidth>
                   Upload Resume (PDF/Doc)
                   <input
                     type="file"
                     hidden
                     accept=".pdf,.doc,.docx"
                     onChange={(e) => {
                       if (e.target.files[0]) {
                         setResume(e.target.files[0]);
                         setResumeName(e.target.files[0].name);
                       }
                     }}
                   />
                 </Button>
                 {resumeName && (
                   <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                     Selected: {resumeName}
                   </Typography>
                 )}
               </Box>
             </>
           )
         ) : (
           <>
             <TextField
               fullWidth
               label="Education"
               multiline
               rows={2}
               value={education}
               onChange={(e) => setEducation(e.target.value)}
               margin="normal"
               required
               placeholder="E.g. B.Tech in Computer Science from XYZ University"
             />

             <TextField
               fullWidth
               label="Previous Experience"
               multiline
               rows={2}
               value={experience}
               onChange={(e) => setExperience(e.target.value)}
               margin="normal"
               required
               placeholder="E.g. 2 years as Software Developer at ABC Corp"
             />

             <Box sx={{ mt: 2, mb: 1 }}>
               <Button variant="outlined" component="label" fullWidth>
                 Upload Resume (PDF/Doc)
                 <input
                   type="file"
                   hidden
                   accept=".pdf,.doc,.docx"
                   onChange={(e) => {
                     if (e.target.files[0]) {
                       setResume(e.target.files[0]);
                       setResumeName(e.target.files[0].name);
                     }
                   }}
                 />
               </Button>
               {resumeName && (
                 <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                   Selected: {resumeName}
                 </Typography>
               )}
             </Box>
           </>
         )}

        </DialogContent>
       <DialogActions
         sx={{
           px: 3,
           pb: 3,
           pt: 1,
           gap: 1.5,
         }}
       >
         <Button
           variant="outlined"
           onClick={() => {
             setApplyDialogOpen(false);
             resetApplyFlow();
           }}
           sx={{
             borderRadius: '12px',
             textTransform: 'none',
             fontWeight: 700,
             px: 2.5,
             borderColor: '#cbd5e1',
             color: '#334155',
           }}
         >
           Cancel
         </Button>

         {hasScreeningQuestions ? (
           !showFinalForm ? (
             <Button
               onClick={handleNextScreening}
               variant="contained"
               sx={{
                 borderRadius: '12px',
                 textTransform: 'none',
                 fontWeight: 800,
                 px: 3,
                 background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                 boxShadow: '0 10px 24px rgba(37, 99, 235, 0.25)',
               }}
             >
               Continue
             </Button>
           ) : (
             <Button
               onClick={handleApply}
               variant="contained"
               sx={{
                 borderRadius: '12px',
                 textTransform: 'none',
                 fontWeight: 800,
                 px: 3,
                 background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                 boxShadow: '0 10px 24px rgba(37, 99, 235, 0.25)',
               }}
             >
               Submit Application
             </Button>
           )
         ) : (
           <Button
             onClick={handleApply}
             variant="contained"
             sx={{
               borderRadius: '12px',
               textTransform: 'none',
               fontWeight: 800,
               px: 3,
               background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
               boxShadow: '0 10px 24px rgba(37, 99, 235, 0.25)',
             }}
           >
             Submit Application
           </Button>
         )}
       </DialogActions>
      </Dialog>
    </Container>
  );
}

export default JobDetails;
